import { useRouterState } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";
import { usePageViewTracking } from "~/hooks/use-page-view-tracking";
import { getVisitorGeo, type VisitorGeo } from "~/lib/geo";
import { hasSecretUrl } from "~/lib/secret-url";
import { getLocale } from "~/paraglide/runtime.js";
import { ConsentManager } from "./provider";

/**
 * Everything on the page that talks to a third party: the consent manager that
 * starts the tag manager, analytics and pixels, and the page-view events sent
 * to them.
 *
 * A route can declare its URL a credential (`SECRET_URL`, e.g. a reviewer's
 * preview link). Every one of those tools would be handed that URL, so a
 * document that opens on one never starts them. The answer is fixed on the
 * first render: swapping the consent provider in or out while the reader
 * navigates would remount the whole page, and a reviewer who clicks on from
 * the preview should not be measured mid-session either.
 */
export function ThirdPartyLayer({
  children,
}: {
  readonly children: ReactNode;
}) {
  const [geo, setGeo] = useState<VisitorGeo | undefined>(undefined);

  // Resolve visitor geo on the client only. Keeping it out of SSR makes the
  // rendered document identical for every visitor of a URL (safe to CDN-cache);
  // the consent jurisdiction and analytics country are corrected per visitor
  // after hydration via ConsentManager/GeoOverrideSync.
  useEffect(() => {
    let cancelled = false;
    getVisitorGeo()
      .then((result) => {
        if (!cancelled) {
          setGeo(result);
        }
      })
      .catch(() => {
        // Non-fatal: consent stays on the safe opt-in default.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onSecretUrl = useRouterState({
    select: (state) => hasSecretUrl(state.matches),
  });
  const [openedOnSecretUrl] = useState(onSecretUrl);

  usePageViewTracking({
    country: geo?.country,
    enabled: !onSecretUrl,
    locale: getLocale(),
  });

  if (openedOnSecretUrl) {
    return children;
  }
  return <ConsentManager geo={geo}>{children}</ConsentManager>;
}
